import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { change, type ExchangeResult, list, type Product } from '@/entities/exchange';
import { toBannerLinks } from '@/shared/lib/banner';
import { safeSrc } from '@/shared/lib/image';
import { useAsync } from '@/shared/lib/use-async';
import { BannerList, BannerSlider } from '@/shared/ui/banner-slider/banner-slider';
import { Button, ButtonGroup } from '@/shared/ui/button/button';
import { Loading } from '@/shared/ui/loading/loading';
import { Notice } from '@/shared/ui/notice/notice';
import { Alert } from '@/shared/ui/popup/alert';
import { Popup } from '@/shared/ui/popup/popup';
import './change.css';

const EXCHANGE_FAILED = '잠시후 다시 시도해주세요.';

function Thumbnail({ product }: { product: Product }) {
  const [proxyFailed, setProxyFailed] = useState(false);
  const proxy = proxyFailed ? null : product.imageProxy;
  return (
    <img
      src={proxy ?? safeSrc(product.image)}
      alt={`${product.brand} ${product.name}`}
      loading="lazy"
      onError={proxy === null ? undefined : () => setProxyFailed(true)}
    />
  );
}

export interface ChangePageProps {
  preloaded?: Extract<ExchangeResult, { status: 'ok' }>;
  token?: string | null;
}

export function ChangePage({ preloaded, token }: ChangePageProps = {}) {
  const { seqnumber = '' } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync<ExchangeResult>(
    () => (preloaded ? Promise.resolve(preloaded) : list(seqnumber)),
    [seqnumber, preloaded],
  );

  const [selected, setSelected] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [failOpen, setFailOpen] = useState(false);
  const [failMessage, setFailMessage] = useState('');
  const [popupOpen, setPopupOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const ctaRef = useRef<HTMLButtonElement>(null);

  const result = data?.status === 'ok' ? data : null;

  useEffect(() => {
    if (data?.status === 'changed') navigate(`/coupon/${data.seqnumber}`, { replace: true });
  }, [data, navigate]);

  const products = result?.products ?? [];
  const selectedProduct: Product | undefined = products.find((product) => product.subcode === selected);

  function handleCtaClick() {
    if (!selectedProduct) {
      setAlertOpen(true);
      return;
    }
    setPopupOpen(true);
  }

  function handleClosePopup() {
    if (submitting) return;
    setPopupOpen(false);
  }

  function fail(message: string, cause?: unknown) {
    if (cause !== undefined) {
      console.error('[gifcon-changecon] 쿠폰 교환 실패', cause instanceof Error ? cause.name : 'UNKNOWN');
    }
    setPopupOpen(false);
    setFailMessage(message);
    setFailOpen(true);
    setSubmitting(false);
  }

  async function handleConfirmExchange() {
    if (submitting || !selectedProduct) return;
    setSubmitting(true);
    try {
      const res = await change(token ?? seqnumber, selectedProduct.subcode);
      if (res.success && res.seqnumber) {
        navigate(`/coupon/${res.seqnumber}`, { replace: true, state: { justExchanged: true } });
        return;
      }
      fail(res.success ? EXCHANGE_FAILED : res.message);
    } catch (cause) {
      fail(EXCHANGE_FAILED, cause);
    }
  }

  function renderList() {
    if (products.length === 0) {
      return (
        <div className="change-page__empty">
          <p className="change-page__empty-reason">교환하실 수 있는 쿠폰이 없어요 😥</p>
        </div>
      );
    }
    return (
      <fieldset className="change-page__fieldset">
        <legend className="sr-only">교환할 상품 선택</legend>
        <ul className="change-product-list">
          {products.map((product, index) => {
            const inputId = `change-product-${index}`;
            return (
              <li key={product.subcode}>
                <input
                  type="radio"
                  name="subcode"
                  id={inputId}
                  value={product.subcode}
                  checked={product.subcode === selected}
                  onChange={() => setSelected(product.subcode)}
                />
                <label htmlFor={inputId}>
                  <div className="change-product-list__content">
                    <figure className="change-product-list__thumbnail">
                      <Thumbnail product={product} />
                    </figure>
                    <small className="change-product-list__brand">{product.brand}</small>
                    <span className="change-product-list__name">{product.name}</span>
                  </div>
                </label>
              </li>
            );
          })}
        </ul>
      </fieldset>
    );
  }

  if (loading) {
    return (
      <section className="change-state">
        <p role="status">교환 정보를 확인하는 중이에요…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="change-state">
        <p>교환 정보를 불러오지 못했어요 😥</p>
        <button type="button" className="change-state__retry" onClick={reload}>
          다시 시도
        </button>
      </section>
    );
  }

  if (data && (data.status === 'expired' || data.status === 'error')) return <Notice message={data.message} />;

  if (!result) return null;

  return (
    <section className="change-page">
      <h1 className="sr-only">쿠폰 교환</h1>

      <div className="change-page__inner change-page__content">
        <div className="change-page__banner">
          <BannerSlider slides={toBannerLinks(result.topBanners)} />
        </div>

        <div className="title-box">
          <h2>원하는 쿠폰으로 교환하세요!</h2>
          <small>아래 상품 중 하나를 선택하여 교환하실 수 있습니다.</small>
        </div>

        <div className="change-page__list-wrap" aria-live="polite">
          {renderList()}
        </div>
      </div>

      <div className="change-page__fixed-bottom">
        <div className="change-page__inner">
          <ButtonGroup>
            <Button ref={ctaRef} variant="primary" onClick={handleCtaClick} disabled={submitting}>
              바꿔쓰기
            </Button>
          </ButtonGroup>
          <BannerList className="change-page__bottom-banners" slides={toBannerLinks(result.bottomBanners)} />
        </div>
      </div>

      <Alert
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        triggerRef={ctaRef}
        title="교환 실패"
        message="교환할 상품을 선택해주세요."
      />

      <Alert
        open={failOpen}
        onClose={() => setFailOpen(false)}
        triggerRef={ctaRef}
        title="교환 실패"
        message={failMessage}
      />

      <Popup
        open={popupOpen}
        onClose={handleClosePopup}
        triggerRef={ctaRef}
        title="쿠폰 교환을 진행하시겠습니까?"
        description={
          <>
            쿠폰을 교환을 완료한 후에는
            <br />
            <strong>기존 쿠폰으로 되돌릴 수 없습니다.</strong>
          </>
        }
        box={selectedProduct ? { heading: '교환 상품', description: selectedProduct.name } : undefined}
        footer={
          <ButtonGroup>
            <Button variant="secondary" onClick={handleClosePopup} disabled={submitting}>
              취소하기
            </Button>
            <Button variant="primary" onClick={handleConfirmExchange} disabled={submitting}>
              바꿔쓰기
            </Button>
          </ButtonGroup>
        }
      />

      <Loading open={submitting} label="교환 처리 중" />
    </section>
  );
}
